import React, { useState, useEffect, useRef, useContext } from 'react';
import { getDatePreset, todayET } from '../utils/formatters';
import useIsMobile from '../hooks/useIsMobile';
import { DateRangeContext } from '../contexts/DateRangeContext';

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay()); // back up to Sunday
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

// Single-box range picker. First click sets the start, second sets the end
// (auto-swapped if before start). Days between are tinted, start/end are
// rendered darker. Hovering after the first click previews the range.
function CalendarRangePicker({ value, onChange, max }) {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState(null);
  const [hoverISO, setHoverISO] = useState(null);

  const seed = value?.startDate ? new Date(value.startDate + 'T12:00:00') : new Date();
  const [viewYear, setViewYear] = useState(seed.getFullYear());
  const [viewMonth, setViewMonth] = useState(seed.getMonth());

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setPendingStart(null);
        setHoverISO(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Open-time reset is handled in the toggle button's onClick — see below.
  // We deliberately do NOT reset pendingStart on `value?.startDate` change,
  // because clicking the first day commits a partial { startDate, endDate: '' }
  // up the tree, and a reset there would erase the in-flight pending state
  // and treat the next click as another "first click".

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  function handleDayClick(d) {
    const iso = isoDate(d);
    if (max && iso > max) return;
    if (!pendingStart) {
      setPendingStart(iso);
      onChange({ startDate: iso, endDate: '' });
    } else {
      let start = pendingStart;
      let end = iso;
      if (end < start) [start, end] = [end, start];
      setPendingStart(null);
      setHoverISO(null);
      onChange({ startDate: start, endDate: end });
      setOpen(false);
    }
  }

  // The committed range while the picker is closed (or both dates locked in).
  // While the user is mid-pick we use pendingStart + hoverISO instead.
  const lockedStart = pendingStart ? null : (value?.startDate || null);
  const lockedEnd   = pendingStart ? null : (value?.endDate || null);
  const previewStart = pendingStart;
  const previewEnd   = pendingStart && hoverISO ? hoverISO : null;

  function inRange(iso) {
    if (lockedStart && lockedEnd) return iso >= lockedStart && iso <= lockedEnd;
    if (previewStart && previewEnd) {
      const lo = previewStart < previewEnd ? previewStart : previewEnd;
      const hi = previewStart < previewEnd ? previewEnd : previewStart;
      return iso >= lo && iso <= hi;
    }
    if (previewStart) return iso === previewStart;
    return false;
  }

  function isEdge(iso) {
    if (lockedStart === iso || lockedEnd === iso) return true;
    if (previewStart === iso || previewEnd === iso) return true;
    return false;
  }

  const grid = getMonthGrid(viewYear, viewMonth);

  function formatShortLocal(s) {
    if (!s) return '';
    const [y, m, d] = s.split('-');
    return `${parseInt(m)}/${parseInt(d)}/${y.slice(2)}`;
  }

  const label = value?.startDate && value?.endDate
    ? `${formatShortLocal(value.startDate)} – ${formatShortLocal(value.endDate)}`
    : value?.startDate
      ? `${formatShortLocal(value.startDate)} – pick end…`
      : 'Pick a date range';

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => {
          if (!open) {
            // Opening: clear in-flight pick state and jump the calendar to
            // the saved start's month (or today if none).
            const target = value?.startDate ? new Date(value.startDate + 'T12:00:00') : new Date();
            setViewYear(target.getFullYear());
            setViewMonth(target.getMonth());
            setPendingStart(null);
            setHoverISO(null);
          }
          setOpen(o => !o);
        }}
        style={{
          padding: '6px 12px',
          fontSize: 13,
          border: '1px solid var(--border)',
          background: 'white',
          borderRadius: 4,
          cursor: 'pointer',
          minWidth: 200,
          textAlign: 'left',
          color: value?.startDate ? 'var(--text)' : 'var(--text-light)',
        }}
      >
        {label}
      </button>
      {open && (
        <div className="cal-popover"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 12,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            zIndex: 1000,
            width: 280,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button type="button" onClick={prevMonth}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '0 8px', color: 'var(--text-secondary)' }}>‹</button>
            <strong style={{ fontSize: 13 }}>
              {new Date(viewYear, viewMonth, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </strong>
            <button type="button" onClick={nextMonth}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '0 8px', color: 'var(--text-secondary)' }}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text-light)', padding: 2 }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {grid.map((d, i) => {
              const iso = isoDate(d);
              const isCurrentMonth = d.getMonth() === viewMonth;
              const disabled = max && iso > max;
              const rng = inRange(iso);
              const edge = isEdge(iso);
              const background = edge ? '#1e3a8a' : rng ? '#dbeafe' : 'transparent';
              const color = edge ? 'white'
                : disabled ? '#cbd5e1'
                : isCurrentMonth ? 'var(--text)'
                : '#cbd5e1';
              return (
                <button className="cal-day"
                  key={i}
                  type="button"
                  onClick={() => handleDayClick(d)}
                  onMouseEnter={() => { if (pendingStart) setHoverISO(iso); }}
                  disabled={disabled}
                  style={{
                    border: 'none',
                    padding: '7px 0',
                    fontSize: 12,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    background,
                    color,
                    fontWeight: edge ? 700 : 400,
                    borderRadius: 4,
                    opacity: disabled ? 0.4 : 1,
                  }}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          {pendingStart && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-light)', textAlign: 'center' }}>
              Click the end of your range
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const STORAGE_KEY = 'nucleus_saved_date_presets';

function loadSavedPresets() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
}

function saveSavedPresets(presets) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
}

const BUILT_IN = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_7d', label: 'Last 7D' },
  { key: 'last_30d', label: 'Last 30D' },
  { key: 'last_90d', label: 'Last 90D' },
  { key: 'year', label: 'This Year' },
  { key: 'lifetime', label: 'Lifetime' },
];

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y.slice(2)}`;
}

// `allowFuture` is for pages whose rows can be scheduled ahead of time (the
// Breaks list). It widens the period presets past today (see getDatePreset's
// includeFuture) and lets the custom picker select future dates. Reporting
// pages leave it off so their ranges still stop at today.
// `onChange(range, meta)` — `meta` is `{ preset, label }` naming the button
// that produced the range, or `label: null` for a hand-picked custom range.
// It is a second argument rather than extra fields on `range` because callers
// spread the range straight into API query params.
export function presetLabel(key) {
  if (!key || key === 'custom') return null;
  if (key.startsWith('saved:')) return key.slice(6);
  return BUILT_IN.find(p => p.key === key)?.label || null;
}

export default function DateRangeFilter({ onChange, initial = 'month', initialCustom, allowFuture = false }) {
  const [preset, setPreset] = useState(initial);
  const [custom, setCustom] = useState(initialCustom || { startDate: '', endDate: '' });
  const [savedPresets, setSavedPresets] = useState(loadSavedPresets);
  const [saveName, setSaveName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [activeRange, setActiveRange] = useState(() => initialCustom || getDatePreset(initial, { includeFuture: allowFuture }));
  const isMobile = useIsMobile();
  // Publish the active range so exported charts and tables can state it.
  const publishedRange = useContext(DateRangeContext);

  useEffect(() => { saveSavedPresets(savedPresets); }, [savedPresets]);

  function handlePreset(p) {
    setPreset(p);
    if (p !== 'custom') {
      const range = getDatePreset(p, { includeFuture: allowFuture });
      setActiveRange(range);
      onChange(range, { preset: p, label: presetLabel(p) });
    }
  }

  function handleSavedPreset(sp) {
    setPreset('saved:' + sp.name);
    const range = { startDate: sp.startDate, endDate: sp.endDate };
    setActiveRange(range);
    onChange(range, { preset: 'saved:' + sp.name, label: sp.name });
  }

  function handleCustomChange(field, value) {
    const next = { ...custom, [field]: value };
    setCustom(next);
    if (next.startDate && next.endDate) {
      setActiveRange(next);
      onChange(next, { preset: 'custom', label: null });
    }
  }

  function saveCustomPreset() {
    if (!saveName.trim() || !custom.startDate || !custom.endDate) return;
    setSavedPresets([...savedPresets, { name: saveName.trim(), startDate: custom.startDate, endDate: custom.endDate }]);
    setSaveName('');
    setShowSaveForm(false);
  }

  function deleteSavedPreset(name) {
    if (!window.confirm(`Delete preset "${name}"?`)) return;
    setSavedPresets(savedPresets.filter(p => p.name !== name));
    if (preset === 'saved:' + name) handlePreset('today');
  }

  function handleDropdownChange(e) {
    const val = e.target.value;
    if (val === 'custom') {
      handlePreset('custom');
    } else if (val.startsWith('saved:')) {
      const sp = savedPresets.find(s => 'saved:' + s.name === val);
      if (sp) handleSavedPreset(sp);
    } else {
      handlePreset(val);
    }
  }

  useEffect(() => {
    if (!publishedRange) return;
    publishedRange.current = activeRange;
    // Clear on unmount so a page without a filter can't export a stale range.
    return () => { publishedRange.current = null; };
  }, [activeRange, publishedRange]);

  const activeLabel = BUILT_IN.find(p => p.key === preset)?.label
    || savedPresets.find(s => 'saved:' + s.name === preset)?.name
    || (preset === 'custom' ? 'Custom' : preset);

  return (
    <div className="date-filter">
      {isMobile ? (
        <select value={preset} onChange={handleDropdownChange}
          style={{ flex: 1, padding: '8px 12px', fontSize: '14px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)' }}>
          {BUILT_IN.map(p => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
          {savedPresets.map(sp => (
            <option key={sp.name} value={'saved:' + sp.name}>{sp.name}</option>
          ))}
          <option value="custom">Custom</option>
        </select>
      ) : (
        <>
          {BUILT_IN.map(p => (
            <button key={p.key} className={`date-preset ${preset === p.key ? 'active' : ''}`} onClick={() => handlePreset(p.key)}>
              {p.label}
            </button>
          ))}
          {savedPresets.map(sp => (
            <span key={sp.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
              <button className={`date-preset ${preset === 'saved:' + sp.name ? 'active' : ''}`}
                onClick={() => handleSavedPreset(sp)}>
                {sp.name}
              </button>
              {preset === 'saved:' + sp.name && (
                <button onClick={() => deleteSavedPreset(sp.name)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '16px', padding: '0 2px', lineHeight: 1 }}
                  title="Delete preset">&times;</button>
              )}
            </span>
          ))}
          <button className={`date-preset ${preset === 'custom' ? 'active' : ''}`} onClick={() => handlePreset('custom')}>
            Custom
          </button>
        </>
      )}
      {preset === 'custom' && (
        <div className="date-inputs" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <CalendarRangePicker
            value={custom}
            onChange={(range) => {
              setCustom(range);
              if (range.startDate && range.endDate) {
                setActiveRange(range);
                onChange(range, { preset: 'custom', label: null });
              }
            }}
            max={allowFuture ? undefined : todayET()}
          />
          {custom.startDate && custom.endDate && !showSaveForm && (
            <button className="btn btn-secondary btn-sm" onClick={() => setShowSaveForm(true)}>Save as Preset</button>
          )}
          {showSaveForm && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <input type="text" placeholder="Preset name" value={saveName} onChange={e => setSaveName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveCustomPreset(); }}
                style={{ width: '140px', padding: '4px 8px', fontSize: '12px' }} />
              <button className="btn btn-primary btn-sm" onClick={saveCustomPreset}>Save</button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowSaveForm(false); setSaveName(''); }}>Cancel</button>
            </span>
          )}
        </div>
      )}
      {activeRange && activeRange.startDate && (
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {activeRange.startDate === activeRange.endDate
            ? formatShortDate(activeRange.startDate)
            : activeRange.endDate
              ? `${formatShortDate(activeRange.startDate)} - ${formatShortDate(activeRange.endDate)}`
              : `${formatShortDate(activeRange.startDate)} - onward`}
        </span>
      )}
    </div>
  );
}
